import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTenant } from '../../context/TenantContext'; 
import { 
  getOperatorCommissions, 
  getPendingLiabilities, 
  getCommissionRules, 
  saveCommissionRules,
  getOperatorPulse 
} from '../../services/db'; 

export default function CommissionDashboard({ isOperatorView = false }) {
  const { currentUser } = useAuth();
  const { tenantId, loading: tenantLoading } = useTenant(); 
  const navigate = useNavigate(); 
  
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('tiers'); 

  // ==========================================
  // STATE: OPERATOR PERSPECTIVE
  // ==========================================
  const [operatorStats, setOperatorStats] = useState({
    name: "", currentTier: "Standard 1099", payoutRate: "TBD", nextTier: "N/A", mrrToNextTier: 0, mtdEarnings: 0, pendingPayout: 0, ytdEarnings: 0,
  });
  const [myDeals, setMyDeals] = useState([]);

  // ==========================================
  // STATE: FOUNDER PERSPECTIVE
  // ==========================================
  const [pendingLiabilities, setPendingLiabilities] = useState([]);
  
  const [rules, setRules] = useState({
    effectiveDate: '',
    payoutCycle: 'Monthly (1st)',
    tiers: [],
    spiffs: [],
    accelerators: [],
    clawbacks: []
  });

  // --- DYNAMIC DELETION MODAL STATE ---
  const [deleteTarget, setDeleteTarget] = useState(null);

  // --- ⚡️ LIVE FIREBASE FETCH ⚡️ ---
  useEffect(() => {
    const fetchCommissionData = async () => {
      if (tenantLoading || !tenantId) return;

      setIsLoading(true);
      try {
        const uid = currentUser?.uid;

        if (isOperatorView && !uid) return;

        if (isOperatorView) {
          const pulse = await getOperatorPulse(tenantId, uid) || {};
          const transactions = await getOperatorCommissions(tenantId, uid) || [];

          setOperatorStats({
            name: currentUser?.displayName || currentUser?.email?.split('@')[0] || "Operator",
            currentTier: pulse.tier || 'Standard 1099',
            payoutRate: pulse.payoutRate || "Dynamic", 
            nextTier: pulse.nextTier || "Evaluating...",
            mrrToNextTier: Number(pulse.mrrToNextTier || 0), 
            mtdEarnings: Number(pulse.mtdCommission || 0),
            pendingPayout: Number(pulse.pendingPayout || 0),
            ytdEarnings: Number(pulse.ytdEarnings || 0) 
          });

          setMyDeals(transactions.map(t => ({
            id: t.id,
            client: t.company_name || 'Unknown Client',
            date: t.created_at?.toDate ? t.created_at.toDate().toLocaleDateString() : 'Recent',
            mrr: Number(t.mrr_value || 0),
            commission: Number(t.commission_amount || 0),
            status: t.status || 'Pending',
            statusColor: t.status === 'Paid' ? 'text-emerald-600 bg-emerald-50 border-emerald-200' : 'text-amber-500 bg-amber-50 border-amber-200'
          })));

        } else {
          const liabilities = await getPendingLiabilities(tenantId) || [];
          setPendingLiabilities(liabilities);

          const fetchedRules = await getCommissionRules(tenantId);
          if (fetchedRules && Object.keys(fetchedRules).length > 0) {
            setRules({
              effectiveDate: fetchedRules.effectiveDate || new Date().toISOString().split('T')[0],
              payoutCycle: fetchedRules.payoutCycle || 'Monthly (1st)',
              tiers: fetchedRules.tiers || [],
              spiffs: fetchedRules.spiffs || [],
              accelerators: fetchedRules.accelerators || [],
              clawbacks: fetchedRules.clawbacks || []
            });
          } else {
            setRules({
              effectiveDate: new Date().toISOString().split('T')[0],
              payoutCycle: 'Monthly (1st)',
              tiers: [], spiffs: [], accelerators: [], clawbacks: []
            });
          }
        }
      } catch (error) {
        console.error("Failed to fetch commission matrix:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCommissionData();
  }, [isOperatorView, currentUser, tenantId, tenantLoading]); 

  // ==========================================
  // DYNAMIC RULE HANDLERS (FOUNDER ONLY)
  // ==========================================
  const handleAddRuleItem = (category, defaultItem) => {
    setRules(prev => ({
      ...prev,
      [category]: [...(prev[category] || []), { id: Date.now().toString(), ...defaultItem }]
    }));
  };

  const confirmRemoveRuleItem = (category, id, descriptor) => {
    setDeleteTarget({ category, id, descriptor });
  };

  const executeRemoveRuleItem = () => {
    if (!deleteTarget) return;
    setRules(prev => ({
      ...prev,
      [deleteTarget.category]: prev[deleteTarget.category].filter(item => item.id !== deleteTarget.id)
    }));
    setDeleteTarget(null);
  };

  const handleRuleChange = (category, id, field, value) => {
    setRules(prev => ({
      ...prev,
      [category]: prev[category].map(item => item.id === id ? { ...item, [field]: value } : item)
    }));
  };

  const saveMasterRules = async () => {
    if (!tenantId) {
      alert("System Error: Corporate identity not resolved. Cannot save rules.");
      return;
    }
    
    setIsSaving(true);
    try {
      // Injecting audit trail & version tracking before deploying to fleet
      const deploymentPayload = {
        ...rules,
        versionId: `v_${Date.now()}`, 
        updatedAt: new Date().toISOString(),
        updatedBy: currentUser?.email || 'SYS_ADMIN'
      };
      
      await saveCommissionRules(tenantId, deploymentPayload);
      alert("Success: Master Rules Matrix securely deployed to fleet operations.");
    } catch (error) {
      console.error("Error saving rules", error);
      alert("System Error: Failed to push matrix to the database.");
    } finally {
      setIsSaving(false);
    }
  };

  // ==========================================
  // VIEW RENDERER: OPERATOR
  // ==========================================
  if (isOperatorView) {
    if (isLoading || tenantLoading) return <div className="p-12 text-center text-slate-500 font-bold uppercase tracking-widest flex flex-col items-center"><div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mb-4"></div>Syncing Commission Engine...</div>;

    const totalTarget = operatorStats.mtdEarnings + operatorStats.mrrToNextTier;
    const progressPercent = totalTarget > 0 ? (operatorStats.mtdEarnings / totalTarget) * 100 : 0;

    return (
      <div className="animate-in fade-in duration-500 pb-12 relative h-full">
        <div className="flex justify-between items-end mb-6 border-b border-slate-300 pb-4">
          <div>
            <h1 className="text-2xl font-black text-slate-900 tracking-tight uppercase">My Commission Engine</h1>
            <p className="text-slate-500 text-sm mt-1 font-medium">Track your earnings and progress toward the next payout tier.</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Active Tier</p>
            <p className="text-lg font-black text-emerald-600 uppercase tracking-widest">{operatorStats.currentTier} ({operatorStats.payoutRate})</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-slate-900 border border-slate-800 p-6 shadow-xl flex flex-col justify-center rounded-sm">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Pending Payout</p>
            <p className="text-4xl font-black text-white">${operatorStats.pendingPayout.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
            <p className="text-[9px] font-bold text-amber-500 uppercase tracking-widest mt-2 flex items-center gap-1">
              <i className="fa-solid fa-clock"></i> Awaiting Clearing
            </p>
          </div>
          <div className="bg-white border border-slate-300 p-6 shadow-sm flex flex-col justify-center border-t-4 border-t-emerald-500 rounded-sm">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">MTD Earnings (Cleared)</p>
            <p className="text-4xl font-black text-slate-900">${operatorStats.mtdEarnings.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
          </div>
          <div className="bg-white border border-slate-300 p-6 shadow-sm flex flex-col justify-center rounded-sm">
            <div className="flex justify-between items-end mb-2">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Next Tier: {operatorStats.nextTier}</span>
              <span className="text-[10px] font-black text-emerald-600">${operatorStats.mrrToNextTier.toLocaleString(undefined, {minimumFractionDigits: 0})} MRR to go</span>
            </div>
            <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-emerald-500 transition-all duration-1000" style={{ width: `${progressPercent}%` }}></div>
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-300 shadow-sm rounded-sm overflow-hidden">
          <div className="bg-slate-100 border-b border-slate-300 px-6 py-4">
            <h3 className="text-[10px] font-black text-slate-800 uppercase tracking-widest">Recent Transactions</h3>
          </div>
          <div className="divide-y divide-slate-100">
            {myDeals.length === 0 ? (
              <div className="p-12 text-center text-slate-400 text-[10px] font-bold uppercase tracking-widest">
                <i className="fa-solid fa-receipt text-2xl mb-2 opacity-50 block"></i>
                No commission events recorded.
              </div>
            ) : (
              myDeals.map((deal) => (
                <div key={deal.id} className="p-4 flex items-center justify-between hover:bg-slate-50 transition-colors">
                  <div className="flex-1">
                    <p className="text-sm font-bold text-slate-900 truncate">{deal.client}</p>
                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">{deal.date} • ID: {deal.id.substring(0, 8).toUpperCase()}</p>
                  </div>
                  <div className="flex-1 text-center">
                    <p className="text-xs font-black text-slate-700">${deal.mrr.toLocaleString(undefined, {minimumFractionDigits: 2})} MRR</p>
                    <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">Deal Value</p>
                  </div>
                  <div className="flex-1 text-center">
                    <span className={`border text-[8px] font-black px-2.5 py-1 uppercase tracking-widest rounded-sm ${deal.statusColor}`}>
                      {deal.status}
                    </span>
                  </div>
                  <div className="flex-1 text-right">
                    <p className="text-lg font-black text-emerald-600">+${deal.commission.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    );
  }

  // ==========================================
  // VIEW RENDERER: FOUNDER
  // ==========================================
  if (isLoading || tenantLoading) return <div className="p-12 text-center text-slate-500 font-bold uppercase tracking-widest flex flex-col items-center"><div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mb-4"></div>Syncing Data Center...</div>;

  return (
    <div className="animate-in fade-in duration-500 pb-12 relative h-full flex flex-col">
      
      {/* GLOBAL DELETE CONFIRMATION MODAL */}
      {deleteTarget && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/70 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white border border-slate-300 shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95">
            <div className="bg-red-600 p-5 flex justify-between items-center">
              <h2 className="text-sm font-black text-white uppercase tracking-tight flex items-center gap-2">
                <i className="fa-solid fa-triangle-exclamation"></i> Destructive Action
              </h2>
            </div>
            <div className="p-6 text-center space-y-4">
              <h3 className="text-lg font-black text-slate-900">Delete {deleteTarget.descriptor}?</h3>
              <p className="text-xs font-medium text-slate-600 leading-relaxed">
                You are about to remove a global commission rule. This will instantly alter payout calculations for your entire multi-tenant fleet upon your next deployment.
              </p>
              <p className="text-[10px] font-bold text-red-500 uppercase tracking-widest bg-red-50 p-2 rounded-sm border border-red-100">
                This action cannot be undone.
              </p>
            </div>
            <div className="p-4 bg-slate-50 flex gap-3 border-t border-slate-200">
              <button 
                onClick={() => setDeleteTarget(null)} 
                className="flex-1 bg-white border border-slate-300 text-slate-700 text-[10px] font-bold py-2.5 uppercase tracking-widest hover:bg-slate-100 transition-colors rounded-sm"
              >
                Cancel
              </button>
              <button 
                onClick={executeRemoveRuleItem} 
                className="flex-1 bg-red-600 hover:bg-red-700 text-white text-[10px] font-bold py-2.5 uppercase tracking-widest transition-colors shadow-sm rounded-sm"
              >
                Confirm Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-between items-end mb-6 border-b border-slate-300 pb-4">
        <div>
          <h1 className="text-2xl font-black text-slate-900 tracking-tight uppercase">Commission Matrix & Ledger</h1>
          <p className="text-slate-500 text-sm mt-1 font-medium">Manage payout arrays, promotional spiffs, and review fleet liabilities.</p>
        </div>
        <button 
          onClick={() => navigate('/payroll')} 
          className="bg-slate-900 hover:bg-slate-800 text-white text-[10px] font-bold py-2.5 px-6 transition-colors uppercase tracking-widest shadow-sm flex items-center gap-2 rounded-sm"
        >
          Go to Payroll Center <i className="fa-solid fa-arrow-right"></i>
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        
        {/* LEFT COL: Pending Liabilities */}
        <div className="lg:col-span-4 space-y-6">
          <div className="bg-white border border-slate-300 shadow-sm sticky top-8 rounded-sm overflow-hidden">
            <div className="bg-slate-900 border-b border-slate-800 px-6 py-4">
              <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Pending Liability</h3>
              <p className="text-2xl font-black text-emerald-400 mt-1">
                ${pendingLiabilities.reduce((sum, item) => sum + item.pending, 0).toLocaleString(undefined, {minimumFractionDigits: 2})}
              </p>
            </div>
            <div className="divide-y divide-slate-100">
              {pendingLiabilities.length === 0 ? (
                <div className="p-12 text-center text-slate-400 text-[10px] font-bold uppercase tracking-widest">
                  <i className="fa-solid fa-clipboard-check text-2xl mb-2 opacity-50 block"></i>
                  No pending payouts.
                </div>
              ) : (
                pendingLiabilities.map((rep, idx) => (
                  <div className="p-4 hover:bg-slate-50 transition-colors" key={idx}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-600 font-black text-xs uppercase rounded-sm">
                          {rep.rep.charAt(0)}
                        </div>
                        <div>
                          <p className="text-sm font-bold text-slate-900 truncate max-w-[120px]">{rep.rep}</p>
                          <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{rep.totalDeals} Deals Pending</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-base font-black text-slate-900">${rep.pending.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                      </div>
                    </div>
                    {/* Tactical Adjustment Control */}
                    <div className="flex gap-2">
                      <button className="flex-1 bg-white border border-slate-200 hover:border-emerald-500 hover:text-emerald-600 text-slate-500 text-[9px] font-black py-1.5 uppercase tracking-widest transition-colors flex items-center justify-center gap-1.5 rounded-sm">
                        <i className="fa-solid fa-plus text-[10px]"></i> Manual Adj
                      </button>
                      <button className="flex-1 bg-white border border-slate-200 hover:bg-slate-100 text-slate-600 text-[9px] font-black py-1.5 uppercase tracking-widest transition-colors rounded-sm">
                        Review Deals
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* RIGHT COL: Enterprise Rules Engine */}
        <div className="lg:col-span-8 bg-slate-50 border border-slate-300 shadow-sm flex flex-col min-h-[600px] rounded-sm overflow-hidden">
          
          <div className="bg-white border-b border-slate-300 px-6 py-4 flex justify-between items-center shrink-0">
            <div>
              <h3 className="text-[11px] font-black text-slate-800 uppercase tracking-widest">Global Payout Rules Engine</h3>
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">Configure and deploy compensation logic</p>
            </div>
            <div className="flex gap-4">
              <div>
                <label className="block text-[8px] font-bold text-slate-400 uppercase tracking-widest mb-1">Effective Date</label>
                <input type="date" value={rules.effectiveDate} onChange={(e) => setRules({...rules, effectiveDate: e.target.value})} className="bg-slate-50 border border-slate-200 px-2 py-1 text-[10px] font-bold text-slate-800 focus:outline-none focus:border-emerald-500 rounded-sm uppercase tracking-widest" />
              </div>
              <div>
                <label className="block text-[8px] font-bold text-slate-400 uppercase tracking-widest mb-1">Cycle</label>
                <select value={rules.payoutCycle} onChange={(e) => setRules({...rules, payoutCycle: e.target.value})} className="bg-slate-50 border border-slate-200 px-2 py-1 text-[10px] font-bold text-slate-800 focus:outline-none focus:border-emerald-500 rounded-sm uppercase tracking-widest cursor-pointer">
                  <option>Monthly (1st)</option>
                  <option>Semi-Monthly (1st/15th)</option>
                </select>
              </div>
            </div>
          </div>

          {/* Engine Tabs */}
          <div className="flex border-b border-slate-300 bg-white shrink-0">
            <button onClick={() => setActiveTab('tiers')} className={`flex-1 py-3 text-[10px] font-black uppercase tracking-widest transition-colors border-b-2 ${activeTab === 'tiers' ? 'border-emerald-500 text-emerald-600 bg-slate-50' : 'border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-50'}`}>
              <i className="fa-solid fa-layer-group mr-2"></i> Core Tiers
            </button>
            <button onClick={() => setActiveTab('spiffs')} className={`flex-1 py-3 text-[10px] font-black uppercase tracking-widest transition-colors border-b-2 ${activeTab === 'spiffs' ? 'border-emerald-500 text-emerald-600 bg-slate-50' : 'border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-50'}`}>
              <i className="fa-solid fa-sack-dollar mr-2"></i> Spiff Engine
            </button>
            <button onClick={() => setActiveTab('accelerators')} className={`flex-1 py-3 text-[10px] font-black uppercase tracking-widest transition-colors border-b-2 ${activeTab === 'accelerators' ? 'border-emerald-500 text-emerald-600 bg-slate-50' : 'border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-50'}`}>
              <i className="fa-solid fa-gauge-high mr-2"></i> Accelerators
            </button>
            <button onClick={() => setActiveTab('clawbacks')} className={`flex-1 py-3 text-[10px] font-black uppercase tracking-widest transition-colors border-b-2 ${activeTab === 'clawbacks' ? 'border-red-500 text-red-600 bg-slate-50' : 'border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-50'}`}>
              <i className="fa-solid fa-scale-unbalanced mr-2"></i> Risk & Clawbacks
            </button>
          </div>

          <div className="p-6 flex-1 overflow-y-auto custom-scrollbar bg-slate-50">
            
            {/* TAB: MRR Tiers */}
            {activeTab === 'tiers' && (
              <div className="animate-in fade-in">
                <div className="flex justify-between items-end mb-4 pb-2">
                  <h4 className="text-[10px] font-black text-slate-700 uppercase tracking-widest">Base MRR Volume Tiers</h4>
                  <button onClick={() => handleAddRuleItem('tiers', { name: 'New Tier', targetTier: 'Standard 1099', threshold: 0, rate: 0, residualRate: 0 })} className="text-[9px] font-black text-emerald-600 hover:text-emerald-800 uppercase tracking-widest bg-emerald-50 px-3 py-1 border border-emerald-200 rounded-sm transition-colors">
                    + Add Tier Group
                  </button>
                </div>
                <div className="space-y-3">
                  {rules.tiers?.length === 0 && <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest p-6 border border-dashed border-slate-300 text-center rounded-sm bg-slate-50">No tiers configured.</p>}
                  {rules.tiers?.map((tier) => (
                    <div key={tier.id} className="bg-white border border-slate-200 shadow-sm p-3 rounded-sm flex flex-col gap-3">
                      {/* Row 1: Target Definition */}
                      <div className="flex items-center gap-3">
                        <input type="text" value={tier.name} onChange={(e) => handleRuleChange('tiers', tier.id, 'name', e.target.value)} className="w-1/3 text-xs font-bold text-slate-800 px-2 py-1.5 focus:outline-none bg-slate-50 border border-slate-200 rounded-sm" placeholder="Tier Name" />
                        <select value={tier.targetTier} onChange={(e) => handleRuleChange('tiers', tier.id, 'targetTier', e.target.value)} className="w-1/3 text-[10px] uppercase tracking-widest font-bold text-slate-600 px-2 py-1.5 focus:outline-none bg-slate-50 border border-slate-200 cursor-pointer rounded-sm">
                          <option value="All Fleets">All Fleets</option>
                          <option value="Standard 1099">Standard 1099</option>
                          <option value="Gold 1099">Gold 1099</option>
                          <option value="Platinum 1099">Platinum 1099</option>
                        </select>
                        <div className="flex items-center gap-2 bg-slate-50 px-2 py-1.5 border border-slate-200 rounded-sm flex-1 shrink-0">
                          <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">MRR &gt; $</span>
                          <input type="number" value={tier.threshold} onChange={(e) => handleRuleChange('tiers', tier.id, 'threshold', Number(e.target.value))} className="w-full text-xs font-bold bg-transparent focus:outline-none text-slate-800" />
                        </div>
                        <button onClick={() => confirmRemoveRuleItem('tiers', tier.id, tier.name || 'Tier Rule')} className="px-3 py-1.5 text-[9px] font-black text-red-500 hover:text-white hover:bg-red-500 border border-transparent hover:border-red-600 uppercase tracking-widest rounded-sm transition-colors shrink-0">
                          Delete
                        </button>
                      </div>
                      
                      {/* Row 2: Y1 vs Residual Payouts */}
                      <div className="flex items-center gap-3 border-t border-slate-100 pt-3">
                        <div className="flex items-center gap-2 bg-emerald-50 px-3 py-2 border border-emerald-200 rounded-sm flex-1">
                          <span className="text-[9px] font-black text-emerald-600 uppercase tracking-widest">Months 1-12 (Y1)</span>
                          <input type="number" value={tier.rate} onChange={(e) => handleRuleChange('tiers', tier.id, 'rate', Number(e.target.value))} className="w-full text-sm font-black bg-transparent focus:outline-none text-emerald-700 text-right" placeholder="0" />
                          <span className="text-[10px] font-black text-emerald-600">%</span>
                        </div>
                        <div className="flex items-center gap-2 bg-blue-50 px-3 py-2 border border-blue-200 rounded-sm flex-1">
                          <span className="text-[9px] font-black text-blue-600 uppercase tracking-widest">Month 13+ (Residual)</span>
                          <input type="number" value={tier.residualRate || 0} onChange={(e) => handleRuleChange('tiers', tier.id, 'residualRate', Number(e.target.value))} className="w-full text-sm font-black bg-transparent focus:outline-none text-blue-700 text-right" placeholder="0" />
                          <span className="text-[10px] font-black text-blue-600">%</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* TAB: Spiffs & Bounties */}
            {activeTab === 'spiffs' && (
              <div className="animate-in fade-in">
                <div className="flex justify-between items-end mb-4 pb-2">
                  <h4 className="text-[10px] font-black text-slate-700 uppercase tracking-widest">Targeted Promotional Spiffs</h4>
                  <button onClick={() => handleAddRuleItem('spiffs', { product: 'New Promo', targetSku: '', payoutClass: 'Instant CheckHQ', type: 'Flat', payout: 0, startDate: '', endDate: '' })} className="text-[9px] font-black text-emerald-600 hover:text-emerald-800 uppercase tracking-widest bg-emerald-50 px-3 py-1 border border-emerald-200 rounded-sm transition-colors">
                    + Add Spiff
                  </button>
                </div>
                <div className="space-y-3">
                  {rules.spiffs?.length === 0 && <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest p-6 border border-dashed border-slate-300 text-center rounded-sm bg-slate-50">No spiffs configured.</p>}
                  {rules.spiffs?.map((spiff) => (
                    <div key={spiff.id} className="bg-white border border-slate-200 shadow-sm p-3 rounded-sm">
                      <div className="flex items-center gap-3 mb-2">
                        <input type="text" value={spiff.product} onChange={(e) => handleRuleChange('spiffs', spiff.id, 'product', e.target.value)} className="flex-1 text-xs font-bold text-slate-800 px-2 py-1.5 focus:outline-none bg-slate-50 border border-slate-200 rounded-sm" placeholder="Promo Name (e.g., Q3 Smart Home Activation)" />
                        <input type="text" value={spiff.targetSku} onChange={(e) => handleRuleChange('spiffs', spiff.id, 'targetSku', e.target.value)} className="w-1/3 text-xs font-bold text-slate-800 px-2 py-1.5 focus:outline-none bg-slate-50 border border-slate-200 rounded-sm" placeholder="Target SKU (e.g., SVC-NEST-01)" />
                        <button onClick={() => confirmRemoveRuleItem('spiffs', spiff.id, spiff.product || 'Spiff')} className="px-3 py-1.5 ml-auto text-[9px] font-black text-red-500 hover:text-white hover:bg-red-500 border border-transparent hover:border-red-600 uppercase tracking-widest rounded-sm transition-colors shrink-0">
                          Delete
                        </button>
                      </div>
                      <div className="flex items-center gap-3 mb-2">
                        <select value={spiff.payoutClass || 'Instant CheckHQ'} onChange={(e) => handleRuleChange('spiffs', spiff.id, 'payoutClass', e.target.value)} className="w-1/3 text-[10px] uppercase tracking-widest font-bold text-slate-600 px-2 py-1.5 focus:outline-none bg-slate-50 border border-slate-200 rounded-sm cursor-pointer">
                          <option value="Instant CheckHQ">Instant CheckHQ (POS Cash)</option>
                          <option value="Carrier Reconciled">Carrier Reconciled (30-45 Days)</option>
                        </select>
                        <select value={spiff.type} onChange={(e) => handleRuleChange('spiffs', spiff.id, 'type', e.target.value)} className="w-24 text-[10px] uppercase tracking-widest font-bold text-slate-600 px-2 py-1.5 focus:outline-none bg-slate-50 border border-slate-200 rounded-sm cursor-pointer">
                          <option>Flat</option>
                          <option>Percentage</option>
                        </select>
                        <div className="flex items-center gap-2 bg-amber-50 px-2 py-1.5 border border-amber-200 w-32 rounded-sm shrink-0">
                          <span className="text-[10px] font-black text-amber-600 uppercase tracking-widest">{spiff.type === 'Percentage' ? '%' : '$'}</span>
                          <input type="number" value={spiff.payout} onChange={(e) => handleRuleChange('spiffs', spiff.id, 'payout', Number(e.target.value))} className="w-full text-xs font-black bg-transparent focus:outline-none text-amber-700" />
                        </div>
                      </div>
                      <div className="flex items-center gap-3 pt-3 mt-1 border-t border-slate-100">
                         <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Promo Window:</span>
                         <input type="date" value={spiff.startDate} onChange={(e) => handleRuleChange('spiffs', spiff.id, 'startDate', e.target.value)} className="text-[10px] font-bold text-slate-600 px-2 py-1 border border-slate-200 uppercase tracking-widest rounded-sm" />
                         <span className="text-[9px] font-black text-slate-300">TO</span>
                         <input type="date" value={spiff.endDate} onChange={(e) => handleRuleChange('spiffs', spiff.id, 'endDate', e.target.value)} className="text-[10px] font-bold text-slate-600 px-2 py-1 border border-slate-200 uppercase tracking-widest rounded-sm" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* TAB: Accelerators */}
            {activeTab === 'accelerators' && (
              <div className="animate-in fade-in">
                <div className="flex justify-between items-end mb-4 pb-2">
                  <h4 className="text-[10px] font-black text-slate-700 uppercase tracking-widest">Performance Multipliers</h4>
                  <button onClick={() => handleAddRuleItem('accelerators', { name: 'New Condition', type: 'Multiplier', value: '' })} className="text-[9px] font-black text-emerald-600 hover:text-emerald-800 uppercase tracking-widest bg-emerald-50 px-3 py-1 border border-emerald-200 rounded-sm transition-colors">
                    + Add Accelerator
                  </button>
                </div>
                <div className="space-y-3">
                  {rules.accelerators?.length === 0 && <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest p-6 border border-dashed border-slate-300 text-center rounded-sm bg-slate-50">No accelerators configured.</p>}
                  {rules.accelerators?.map((acc) => (
                    <div key={acc.id} className="bg-white border border-slate-200 shadow-sm p-3 flex items-center gap-3 rounded-sm">
                      <input type="text" value={acc.name} onChange={(e) => handleRuleChange('accelerators', acc.id, 'name', e.target.value)} className="flex-1 text-xs font-bold text-slate-800 px-2 py-1.5 focus:outline-none bg-slate-50 border border-slate-200 rounded-sm" placeholder="Condition (e.g., Annual Upfront Paid)" />
                      <select value={acc.type} onChange={(e) => handleRuleChange('accelerators', acc.id, 'type', e.target.value)} className="w-32 text-[10px] uppercase tracking-widest font-bold text-slate-600 px-2 py-1.5 focus:outline-none bg-slate-50 border border-slate-200 rounded-sm">
                        <option>Multiplier</option>
                        <option>Bonus %</option>
                      </select>
                      <div className="flex items-center gap-2 bg-blue-50 px-2 py-1.5 border border-blue-200 w-32 rounded-sm shrink-0">
                        <input type="text" value={acc.value} onChange={(e) => handleRuleChange('accelerators', acc.id, 'value', e.target.value)} className="w-full text-xs font-black bg-transparent focus:outline-none text-blue-700 text-center" placeholder="e.g. 1.2x" />
                      </div>
                      <button onClick={() => confirmRemoveRuleItem('accelerators', acc.id, acc.name || 'Accelerator')} className="px-3 py-1.5 ml-auto text-[9px] font-black text-red-500 hover:text-white hover:bg-red-500 border border-transparent hover:border-red-600 uppercase tracking-widest rounded-sm transition-colors shrink-0">
                        Delete
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* TAB: Clawbacks */}
            {activeTab === 'clawbacks' && (
              <div className="animate-in fade-in">
                <div className="flex justify-between items-end mb-4 pb-2">
                  <h4 className="text-[10px] font-black text-slate-700 uppercase tracking-widest">Risk & Reversals</h4>
                  <button onClick={() => handleAddRuleItem('clawbacks', { condition: 'New Penalty Rule', penalty: '' })} className="text-[9px] font-black text-red-600 hover:text-red-800 uppercase tracking-widest bg-red-50 px-3 py-1 border border-red-200 rounded-sm transition-colors">
                    + Add Clawback
                  </button>
                </div>
                <div className="space-y-3">
                  {rules.clawbacks?.length === 0 && <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest p-6 border border-dashed border-slate-300 text-center rounded-sm bg-slate-50">No clawbacks configured.</p>}
                  {rules.clawbacks?.map((claw) => (
                    <div key={claw.id} className="bg-white border border-slate-200 shadow-sm p-3 flex items-center gap-3 border-l-4 border-l-red-500 rounded-sm">
                      <input type="text" value={claw.condition} onChange={(e) => handleRuleChange('clawbacks', claw.id, 'condition', e.target.value)} className="flex-1 text-xs font-bold text-slate-800 px-2 py-1.5 focus:outline-none bg-slate-50 border border-slate-200 rounded-sm" placeholder="Condition (e.g., Early Churn < 90 Days)" />
                      <input type="text" value={claw.penalty} onChange={(e) => handleRuleChange('clawbacks', claw.id, 'penalty', e.target.value)} className="w-48 text-[10px] uppercase tracking-widest font-black text-red-600 bg-red-50 px-3 py-1.5 focus:outline-none border border-red-200 text-center rounded-sm shrink-0" placeholder="Penalty (e.g., 100% Reversal)" />
                      <button onClick={() => confirmRemoveRuleItem('clawbacks', claw.id, claw.condition || 'Clawback')} className="px-3 py-1.5 ml-auto text-[9px] font-black text-red-500 hover:text-white hover:bg-red-500 border border-transparent hover:border-red-600 uppercase tracking-widest rounded-sm transition-colors shrink-0">
                        Delete
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>

          <div className="bg-slate-900 p-4 flex justify-between items-center mt-auto shrink-0">
            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest flex items-center">
              <i className="fa-solid fa-shield-halved text-emerald-500 mr-2 text-[10px]"></i> Changes affect next payout cycle
            </span>
            <button 
              onClick={saveMasterRules} 
              disabled={isSaving}
              className="px-8 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white text-[10px] font-black uppercase tracking-widest transition-colors shadow-sm rounded-sm disabled:opacity-50 flex items-center gap-2"
            >
              {isSaving ? <><i className="fa-solid fa-spinner fa-spin"></i> Deploying...</> : 'Deploy Algorithm to Fleet'}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}